<template>
  <div v-if="seminar && participants">
    <div class="row justify-start q-gutter-lg">
      <Heading1>
        {{ seminar.name }}
      </Heading1>
      <img
        v-if="seminar.type === SeminarType.Idz"
        alt="EU/MŠMT"
        src="https://vys-edu.s3.eu-central-1.amazonaws.com/eu-msmt.png"
        style="max-height: 40px"
      />
      <template v-if="seminar.type === SeminarType.Digcompedu">
        <img
          alt="EU"
          src="https://vys-edu.s3.eu-central-1.amazonaws.com/eu.jpg"
          style="max-height: 40px"
        />
        <img
          alt="EU"
          src="https://vys-edu.s3.eu-central-1.amazonaws.com/plan_narodni_obnovy.jpg"
          style="max-height: 40px"
        />
      </template>
    </div>
    <div class="row items-center q-my-md">
      <q-chip :ripple="false" icon="today">
        {{ formatDateWithTime(seminar.date) }}
        {{ seminar.endDate ? ` - ${formatDateWithTime(seminar.endDate)}` : '' }}
      </q-chip>
      <q-chip :ripple="false" icon="school">
        <span class="">
          {{ translateSeminarType(seminar.type) }}
        </span>
      </q-chip>
      <q-chip v-if="seminar.lector" :ripple="false" icon="school">
        {{ seminar.lector }}
      </q-chip>
      <q-chip v-if="seminar.city" :ripple="false" icon="location_city">
        {{ seminar.city }}
      </q-chip>
      <template v-if="seminar.type === SeminarType.Studium">
        <q-chip
          v-if="seminar.theoryDuration || seminar.practiceDuration"
          :ripple="false"
          icon="timelapse"
        >
          <div class="row items-center q-gutter-sm">
            <span v-if="seminar.theoryDuration">
              Teorie: {{ seminar.theoryDuration }}
            </span>
            <span v-if="seminar.practiceDuration">
              Praxe: {{ seminar.practiceDuration }}
            </span>
          </div>
        </q-chip>
      </template>
      <q-chip v-else-if="seminar.duration" :ripple="false" icon="timelapse">
        {{ seminar.duration }}
      </q-chip>
      <q-chip v-if="seminar.price" :ripple="false" icon="payment">
        {{ seminar.price }} Kč
      </q-chip>
      <q-chip v-if="seminar.deposit" :ripple="false" icon="payment">
        {{ seminar.deposit }} Kč
      </q-chip>

      <q-chip :ripple="false" icon="people">
        <template v-if="participants?.length === 0"> žádný účastník </template>
        <template v-else>
          {{ participants.length }} účastníci
          {{
            substituteParticipants?.length
              ? `+ ${substituteParticipants.length} náhradníků`
              : ''
          }}
        </template>
        <template v-if="seminar.capacity">
          (kapacita {{ seminar.capacity }})
        </template>
      </q-chip>
    </div>
    <div class="row items-center q-my-md q-gutter-sm">
      <q-btn
        class="q-mx-sm"
        color="primary"
        @click="router.push(`/seminar/edit/${seminarId}`)"
      >
        Upravit seminář
      </q-btn>
      <q-btn
        class="q-mx-sm"
        color="secondary"
        @click="isAddParticipantsDialogOpen = true"
      >
        <q-icon :name="matGroupAdd" class="q-mr-xs" size="2.2em" />
        Přidat účastníky
      </q-btn>
      <q-btn
        v-if="registrationLink"
        class="q-mx-sm"
        color="secondary"
        @click="copyRegistrationUrl"
      >
        <q-icon class="q-mr-xs" name="content_copy" size="2.2em" />
        Kopírovat odkaz přihlášky
      </q-btn>
      <q-btn
        v-if="seminar.participants?.length"
        class="q-mx-sm"
        color="secondary"
        outline
        @click="copyParticipantsEmails"
      >
        <q-icon class="q-mr-xs" name="content_copy" size="2.2em" />
        Kopírovat emaily
      </q-btn>
      <q-btn
        :disable="participants.length === 0"
        class="q-mx-sm"
        color="secondary"
        outline
        @click="isAttendanceListDialogOpen = true"
      >
        <q-icon name="download" size="2.2em" />
        Prezenční listina
      </q-btn>
      <q-btn
        :disable="participants.length === 0"
        class="q-mx-sm"
        color="secondary"
        outline
        @click="seminar && generateCertificates(seminar)"
      >
        <q-icon name="download" size="2.2em" />
        Osvědčení
      </q-btn>
      <q-btn
        :disable="participants.length === 0"
        class="q-mx-sm"
        color="secondary"
        outline
        @click="seminar && generateXlsx(seminar)"
      >
        <q-icon name="download" size="2.2em" />
        Excel
      </q-btn>
    </div>

    <q-separator class="q-my-md" />

    <div class="q-mb-lg">
      <ParticipantsTable
        :is-deleting-participants="isDeletingParticipants"
        :seminar="seminar"
        :participants="participants"
        @update:participant="updateParticipant($event)"
        @delete-participants="deleteParticipants($event)"
        @generate-certificates="generateCertificatesForSelected($event)"
      />
    </div>

    <ParticipantsTable
      v-if="substituteParticipants?.length"
      :isSubstituteMode="true"
      :is-deleting-participants="isDeletingParticipants"
      :seminar="seminar"
      :participants="substituteParticipants"
      @update:participant="updateParticipant($event)"
      @delete-participants="deleteParticipants($event)"
      @generate-certificates="generateCertificatesForSelected($event)"
    />

    <GenerateAttendanceListDialog
      v-if="seminar"
      v-model:open="isAttendanceListDialogOpen"
      :seminar="seminar"
      @close="isAttendanceListDialogOpen = false"
    />

    <AddParticipantsDialog
      v-if="seminar && isAddParticipantsDialogOpen"
      v-model:open="isAddParticipantsDialogOpen"
      :isSubmitting="isAddParticipantsSubmitting"
      :seminar="seminar"
      @submit="submitAddParticipants($event)"
    />
  </div>
  <TableSkeleton v-else-if="isLoading" />
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, ref, unref } from 'vue';
import Heading1 from '@/components/common/heading1.vue';
import ParticipantsTable from '@/components/seminar/Participant/ParticipantsTable.vue';
import type { Participant } from '@/components/seminar/Participant/types';
import { useGenerateCertificates } from '@/views/seminars/Documents/generate-certificates';
import type { ParticularSeminar } from '@/components/seminar/SeminarTable/types';
import { copyToClipboard, useQuasar } from 'quasar';
import TableSkeleton from '@/components/skeleton/TableSkeleton.vue';
import GenerateAttendanceListDialog from '@/views/seminars/Documents/GenerateAttendanceListDialog.vue';
import { useGenerateParticipantsXlsx } from '@/views/seminars/Documents/generate-participants-xlsx';
import { GetSeminarQueryDocument } from '@/api/graphql/documents/GetSeminar';
import { useGraphQLQuery } from '@/api/graphql/adapters/use-query';
import { useGraphQLMutation } from '@/api/graphql/adapters';
import { UpdateParticipantMutationDocument } from '@/api/graphql/documents/UpdateParticipant';
import { DeleteParticipantsMutationDocument } from '@/api/graphql/documents/DeleteParticipants';
import {
  type GetSeminarQueryVariables,
  SeminarType,
} from '@/api/graphql/codegen/graphql';
import { useQueryClient } from '@tanstack/vue-query';
import AddParticipantsDialog from '@/views/seminars/Documents/AddParticipantsDialog.vue';
import { AddParticipantsMutationDocument } from '@/api/graphql/documents/AddParticipants';
import type { PartialParticipant } from '@/views/seminars/Documents/types';
import { matGroupAdd } from '@quasar/extras/material-icons';
import { formatDateWithTime } from 'date-formatter';
import { translateSeminarType } from '@/utils/seminarType/translateSeminarType';

const router = useRouter();

const $q = useQuasar();
const queryClient = useQueryClient();

const isAttendanceListDialogOpen = ref(false);
const isAddParticipantsDialogOpen = ref(false);

const { mutate: addParticipantsInput, isPending: isAddParticipantsSubmitting } =
  useGraphQLMutation(AddParticipantsMutationDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

const submitAddParticipants = (participants: PartialParticipant[]) => {
  addParticipantsInput(
    {
      addParticipantsInput: {
        participants: participants.map((p) => {
          const dateParts = p.dateOfBirth
            .trim()
            .replace(' ', '')
            .replace('-', '.')
            .split('.');
          // Extract day, month, and year from the parts
          const day = Number(dateParts[0]);
          const month = Number(dateParts[1]) - 1; // JavaScript months are 0-based (0 = January, 11 = December)
          const year = Number(dateParts[2]);

          const date = new Date(year, month, day);

          return {
            name: p.name,
            dateOfBirth: date.toISOString(),
            seminarId: seminarId.value,
          };
        }),
      },
    },
    {
      onSettled() {
        isAddParticipantsDialogOpen.value = false;
      },
    },
  );
};

const { mutate: mutateUpdateParticipant } = useGraphQLMutation(
  UpdateParticipantMutationDocument,
  {
    mutationKey: ['updateParticipant'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  },
);

const { mutate: deleteParticipantsMutate, isPending: isDeletingParticipants } =
  useGraphQLMutation(DeleteParticipantsMutationDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
const { generateCertificates } = useGenerateCertificates();
const { generateXlsx } = useGenerateParticipantsXlsx();

const generateCertificatesForSelected = async (
  selectedParticipants: Participant[],
) => {
  if (seminar.value) {
    await generateCertificates(seminar.value, unref(selectedParticipants));
  }
};

const route = useRoute();
const seminarId = computed(() => Number(route.params.id));

const getSeminarQueryVariables = computed<GetSeminarQueryVariables>(() => ({
  seminarInput: { seminarId: seminarId.value },
}));
const { data, isLoading, queryKey } = useGraphQLQuery(
  GetSeminarQueryDocument,
  getSeminarQueryVariables,
);

const seminar = computed<ParticularSeminar | null>(() => {
  return data.value?.seminars[0] ?? null;
});

const participants = computed<Participant[] | undefined>(
  () =>
    seminar.value?.participants?.filter((p) => !p.isSubstitute) ?? undefined,
);

const substituteParticipants = computed<Participant[] | undefined>(
  () => seminar.value?.participants?.filter((p) => p.isSubstitute) ?? undefined,
);

const updateParticipant = async ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createdAt,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updatedAt,
  ...newParticipantValue
}: Participant) => {
  return mutateUpdateParticipant({
    participantInput: newParticipantValue,
  });
};

const deleteParticipants = async (selectedParticipantIds: string[]) => {
  $q.dialog({
    title: 'Potvrzení smazání účastníků',
    message: `Opravdu chcete tyto účastníky odstranit? Tato akce nelze vzít zpátky?`,
    color: 'negative',
    ok: `Ano`,
    cancel: true,
  }).onOk(async () => {
    await deleteParticipantsMutate({
      participants: { participantIds: selectedParticipantIds },
    });
  });
};

const copyParticipantsEmails = () => {
  if (!seminar.value) return;

  copyToClipboard(participants.value?.map((p) => p.email).join(';') + ' ').then(
    () => {
      $q.notify({
        message: 'Emaily účastníků byly zkopírovány do schránky',
        color: 'positive',
      });
    },
  );
};

const copyRegistrationUrl = () => {
  if (!seminar.value) return;

  copyToClipboard(registrationLink.value ?? '').then(() => {
    $q.notify({
      message: 'Odkaz na registraci byl zkopírován do schránky',
      color: 'positive',
    });
  });
};

const registrationLink = computed(() =>
  seminar.value
    ? `${import.meta.env.VITE_CLIENT_APP_URL}/seminar/register/${
        seminar.value.id
      }`
    : null,
);

onMounted(() => {
  window.scrollTo(0, 0);
});
</script>
